import React from "react";
import {
  Box,
  Card,
  Chip,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useWod } from "../context/WODContext";
import { ExerciseSet } from "../pages/company/types";
import { millisToReadableFormat } from "../utils/commons";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ViewIcon from "@mui/icons-material/Visibility";

export const SetView = ({
  set,
  index,
  viewOnly,
  isSelected,
  onViewExercisesClicked,
}: {
  isSelected?: boolean;
  viewOnly?: boolean;
  set: ExerciseSet;
  index: number;
  onViewExercisesClicked?: () => void;
}) => {
  const { setCurrentSetIndex, wodSets, setWodSets } = useWod();

  const onEditClick = () => {
    if (viewOnly) {
      onViewExercisesClicked?.();
      return;
    }
    setCurrentSetIndex(index);
  };

  const onDeleteClick = () => {
    let remainingWodSets = wodSets.filter((v) => v.id !== set.id);
    remainingWodSets = remainingWodSets.map((ex, i) => ({
      ...ex,
      id: ++i,
    }));
    setWodSets(remainingWodSets);
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isSelected ? "secondary.main" : "divider",
        bgcolor: isSelected ? "rgba(255,167,38,0.08)" : "background.paper",
        cursor: viewOnly ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: "secondary.main",
          bgcolor: isSelected
            ? "rgba(255,167,38,0.08)"
            : "rgba(255,255,255,0.03)",
        },
      }}
      onClick={viewOnly ? onViewExercisesClicked : undefined}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ flexShrink: 1, minWidth: 0 }}
          >
            {set.name}
          </Typography>
          <Chip
            label={`x${set.repetitions}`}
            size="small"
            color="secondary"
            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title={viewOnly ? "View exercises" : "Edit set"}>
            <IconButton
              size="small"
              onClick={viewOnly ? undefined : onEditClick}
            >
              {viewOnly ? (
                <ViewIcon fontSize="small" />
              ) : (
                <EditIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {!viewOnly && (
            <Tooltip title="Remove set">
              <IconButton
                size="small"
                onClick={onDeleteClick}
                sx={{
                  color: "error.main",
                  "&:hover": { bgcolor: "rgba(244,67,54,0.08)" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {millisToReadableFormat(set.duration)}
        {set.exercises?.length > 0 &&
          ` · ${set.exercises.length} exercise${
            set.exercises.length !== 1 ? "s" : ""
          }`}
      </Typography>
    </Card>
  );
};
